import { useEffect, useMemo, useRef, useState } from "react";
import { soapSections, splitHighlights } from "@/utils/ezyvetModels";
import type { Consult } from "@/types/ezyvet";

interface Props {
  consult: Consult | null;
  /** Phrases to highlight (longest first). */
  terms: string[];
  /** Bump to move focus to the next highlighted mention (keyboard shortcut). */
  jumpSignal?: number;
}

function Highlighted({ text, terms }: { text: string; terms: string[] }) {
  const segments = useMemo(() => splitHighlights(text, terms), [text, terms]);
  return (
    <>
      {segments.map((s, i) =>
        s.match ? (
          <mark key={i} className="term" tabIndex={-1}>
            {s.text}
          </mark>
        ) : (
          <span key={i}>{s.text}</span>
        ),
      )}
    </>
  );
}

/** Left pane: the ezyVet consult's clinical record in SOAP order, flagged procedures highlighted. */
export function SoapViewer({ consult, terms, jumpSignal = 0 }: Props) {
  const root = useRef<HTMLDivElement>(null);
  const [current, setCurrent] = useState(-1);
  const sections = useMemo(() => (consult ? soapSections(consult) : []), [consult]);
  const mentions = useMemo(
    () => sections.reduce((n, s) => n + (s.text ? splitHighlights(s.text, terms).filter((x) => x.match).length : 0), 0),
    [sections, terms],
  );

  const jump = () => {
    const marks = root.current?.querySelectorAll<HTMLElement>("mark.term");
    if (!marks?.length) return;
    const next = (current + 1) % marks.length;
    marks[next].focus(); // styled via mark.term:focus
    marks[next].scrollIntoView?.({ block: "center", behavior: "smooth" });
    setCurrent(next);
  };

  // Keyboard shortcut from the workbench
  const jumpRef = useRef(jump);
  jumpRef.current = jump;
  useEffect(() => {
    if (jumpSignal > 0) jumpRef.current();
  }, [jumpSignal]);

  // A different consult starts the cycle again
  useEffect(() => setCurrent(-1), [consult]);

  return (
    <section className="pane" aria-labelledby="soap-title">
      <header className="pane-header">
        <h2 id="soap-title">Clinical record</h2>
        <div className="row" style={{ gap: 8 }}>
          <span className="muted">{mentions === 1 ? "1 mention" : `${mentions} mentions`}</span>
          <button type="button" className="btn btn-sm" onClick={jump} disabled={!mentions} title="Jump to next mention (n)">
            Next mention <kbd>n</kbd>
          </button>
        </div>
      </header>

      <div className="pane-body" ref={root}>
        {!consult && <p className="muted">ezyVet didn&apos;t return clinical notes for this consult.</p>}
        {sections.map((s) => (
          <section key={s.key} className={`soap-section soap-section--${s.key}`} aria-label={s.label}>
            <h3>
              <span className="soap-letter" aria-hidden="true">
                {s.label[0]}
              </span>
              {s.label}
            </h3>
            {s.text ? (
              <p className="soap-text">
                <Highlighted text={s.text} terms={terms} />
              </p>
            ) : (
              <p className="muted soap-empty">Not recorded</p>
            )}
          </section>
        ))}
      </div>
    </section>
  );
}
